import { Observable } from 'rxjs';
import { Tooth } from '@modules/teeth-diagram/models/tooth';
import type { IntuitiveToothForDiagram } from './intuitive-tooth-for-diagram';
import type { PartialToothUpdate } from '@modules/teeth-diagram/state/teeth-diagram-store';

export const TOOTH_MODAL_ACTION = {
	Save: 'save',
	Delete: 'delete',
	Cancel: 'cancel'
} as const;

type ResultPayload = {
	toothPartialUpdate: PartialToothUpdate | null;
};

/**
 * Standard result type for tooth modals.
 * Returns partial Tooth updates to be applied to all selected teeth,
 * or null if modal was cancelled without changes.
 */
export interface ToothModalResult<P extends ResultPayload = ResultPayload> {
	action: (typeof TOOTH_MODAL_ACTION)[keyof typeof TOOTH_MODAL_ACTION];
	payload: P;
}

/**
 * Standard input data for tooth modals.
 */
export interface ToothModalData {
	/** View models of teeth to display in modal */
	teeth: IntuitiveToothForDiagram[];
	/** Initial tooth data (typically from first selected tooth) */
	initialTooth?: Tooth;
}

/**
 * Optional context for {@link ToothModalService#open}.
 * Implementations may read known keys; callers that do not need options may omit this argument.
 */
export type ToothModalOpenOptions = Readonly<Record<string, unknown>>;

/**
 * Standard interface for tooth modal services.
 * Implement this interface to create a new tooth modal.
 */
export interface ToothModalService {
	/**
	 * Opens the modal with given teeth data.
	 * @param teeth - View models of teeth to display
	 * @param initialTooth - Initial tooth data (typically from first selected tooth)
	 * @param options - Optional modal-specific context (unused unless an implementation reads it)
	 * @returns Observable that emits partial Tooth updates or null if cancelled
	 */
	open(teeth: IntuitiveToothForDiagram[], initialTooth: Tooth, options?: ToothModalOpenOptions): Observable<ToothModalResult>;
}
